import React from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, Rocket, Compass, Wallet, User, Cpu, Link2, MessageCircle } from "lucide-react";
import { AssistantProvider, AssistantFab } from "@/components/earnos/Assistant";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/home", label: "Home", icon: Home },
  { to: "/chat", label: "Chat", icon: MessageCircle },
  { to: "/mission", label: "Work", icon: Cpu },
  { to: "/money", label: "Money", icon: Wallet },
  { to: "/you", label: "You", icon: User },
];
const MORE = [
  { to: "/engine", label: "Engines", icon: Rocket },
  { to: "/opportunities", label: "Explore", icon: Compass },
  { to: "/connections", label: "Connected Apps", icon: Link2 },
];

function SidebarLink({ to, label, icon: Icon }) {
  return (
    <NavLink
      to={to}
      data-testid={`nav-${label.toLowerCase()}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-xl text-base font-semibold transition-colors ${
          isActive ? "bg-brand/15 text-ink border border-brand/30" : "text-ink-2 hover:text-ink hover:bg-surface-3 border border-transparent"
        }`
      }
    >
      <Icon size={20} />
      <span>{label}</span>
    </NavLink>
  );
}

function BottomTab({ to, label, icon: Icon }) {
  return (
    <NavLink
      to={to}
      data-testid={`tab-${label.toLowerCase()}`}
      className={({ isActive }) =>
        `flex-1 flex flex-col items-center justify-center gap-1 py-2 ${isActive ? "text-ink" : "text-ink-2"}`
      }
    >
      <Icon size={22} />
      <span className="text-[11px] font-semibold">{label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  if (pathname === "/chat") {
    return <AssistantProvider>
      <main className="h-dvh w-full overflow-hidden bg-surface-2" aria-label="EARNOS conversation workspace">
        <Outlet />
      </main>
    </AssistantProvider>;
  }
  return (
    <AssistantProvider>
      <div className="min-h-screen bg-surface flex">
        {/* Desktop sidebar */}
        <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-line bg-surface-2/60 p-4 sticky top-0 h-screen">
          <button onClick={() => navigate("/home")} className="flex items-center gap-2 px-2 py-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-light to-brand-violet" />
            <span className="text-xl font-extrabold tracking-widest text-ink">EARNOS</span>
          </button>
          <nav className="space-y-1.5 flex-1">
            {NAV.map((n) => <SidebarLink key={n.to} {...n} />)}
            <div className="border-t border-line mt-4 pt-3 space-y-1">
              <p className="px-4 py-2 text-xs uppercase tracking-wider text-ink-2">Grow and connect</p>
              {MORE.map(n => <SidebarLink key={n.to} {...n} />)}
            </div>
          </nav>
          <div className="mt-4 rounded-xl border border-line bg-surface-3 p-3">
            <div className="text-xs text-ink-2">Signed in as</div>
            <div className="text-sm font-semibold text-ink truncate">{user?.first_name || user?.email}</div>
            <div className="mt-1 text-[11px] uppercase tracking-wide text-brand-light font-bold">{user?.plan || "START"} plan</div>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Mobile top bar */}
          <header className="lg:hidden sticky top-0 z-20 flex items-center justify-between px-4 h-14 border-b border-line bg-surface/90 backdrop-blur">
            <span className="text-lg font-extrabold tracking-widest text-ink">EARNOS</span>
            <span className="text-[11px] uppercase tracking-wide text-brand-light font-bold">{user?.plan || "START"}</span>
          </header>

          <main className="flex-1 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-28 lg:pb-10">
            <div className="lg:hidden flex flex-wrap gap-2 mb-4" aria-label="More EARNOS tools">
              <button onClick={() => navigate("/engine")} className="rounded-full border border-line bg-surface-2 px-3 py-2 text-xs font-semibold text-ink">My engines</button>
              <button onClick={() => navigate("/opportunities")} className="rounded-full border border-line bg-surface-2 px-3 py-2 text-xs font-semibold text-ink">Explore income</button>
              <button onClick={() => navigate("/connections")} className="rounded-full border border-brand/30 bg-brand/10 px-3 py-2 text-xs font-semibold text-ink">Connect apps</button>
            </div>
            <Outlet />
          </main>
        </div>

        {/* Mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 flex border-t border-line bg-surface-2/95 backdrop-blur pb-[env(safe-area-inset-bottom)]">
          {NAV.map((n) => <BottomTab key={n.to} {...n} />)}
        </nav>

        <AssistantFab />
      </div>
    </AssistantProvider>
  );
}
